declare const $CombinedState: unique symbol
export type CombinedState<S> = { readonly [$CombinedState]?: undefined } & S
export type Reducer<S = any, A extends IBasicAction = IAnyAction> = (
  state: S | undefined,
  action: A
) => S
export interface IAnyAction extends IBasicAction {
  // Allows any extra properties to be defined in an action.
  [extraProps: string]: any
}
export interface IBasicAction<T = any> {
  type: T
}
export type ReducersMapObject<
  S = any,
  A extends IBasicAction = IBasicAction
> = {
  [K in keyof S]: Reducer<S[K], A>
}
export type StateFromReducersMapObject<M> = M extends ReducersMapObject<
  any,
  any
>
  ? { [P in keyof M]: M[P] extends Reducer<infer S, any> ? S : never }
  : never
export type ActionFromReducersMapObject<M> = M extends ReducersMapObject<
  any,
  any
>
  ? ActionFromReducer<ReducerFromReducersMapObject<M>>
  : never
export type ActionFromReducer<R> = R extends Reducer<any, infer A> ? A : never
export type ReducerFromReducersMapObject<M> = M extends {
  [P in keyof M]: infer R
}
  ? R extends Reducer<any, any>
    ? R
    : never
  : never

export interface IEmptyPayload {}

type ActionValue<C, T extends keyof C> = C[T]
interface IAction<TypesAndValues, Type extends keyof TypesAndValues> {
  type: Type
  formId?: string
  value: ActionValue<TypesAndValues, Type>
}
interface ISimpleAction<TypesAndValues, Type extends keyof TypesAndValues> {
  type: Type
  formId?: string
}
export type Action<T> = {
  [M in keyof T]: ActionValue<T, M> extends undefined
    ? ISimpleAction<T, M>
    : IAction<T, M>
}[keyof T]

export default function combineReducers<S>(
  reducers: ReducersMapObject<S, any>
): Reducer<CombinedState<S>>
export default function combineReducers<S, A extends IBasicAction = IAnyAction>(
  reducers: ReducersMapObject<S, A>
): Reducer<CombinedState<S>, A>
export default function combineReducers<M extends ReducersMapObject<any, any>>(
  reducers: M
): Reducer<
  CombinedState<StateFromReducersMapObject<M>>,
  ActionFromReducersMapObject<M>
>
// export default function combineReducers<B extends IBasicAction = IAnyAction>(
//   reducers: ReducersMapObject<any, B>
// ): Reducer<
//   CombinedState<StateFromReducersMapObject<any>>,
//   ActionFromReducersMapObject<B>
// >
export default function combineReducers(reducers: ReducersMapObject) {
  const reducerKeys = Object.keys(reducers)
  return (
    state: StateFromReducersMapObject<typeof reducers> = {},
    action: IAnyAction
  ) => {
    let hasChanged = false
    const nextState: StateFromReducersMapObject<typeof reducers> = {}
    // tslint:disable-next-line: prefer-for-of
    for (let i = 0; i < reducerKeys.length; i++) {
      const key = reducerKeys[i]
      const reducer = reducers[key]
      const previousStateForKey = state[key]
      const nextStateForKey = reducer(previousStateForKey, action)
      if (typeof nextStateForKey === "undefined") {
        const errorMessage =
          "undefined key as next state in custom combined reducers : " + key
        throw new Error(errorMessage)
      }
      nextState[key] = nextStateForKey
      hasChanged = hasChanged || nextStateForKey !== previousStateForKey
    }
    hasChanged = hasChanged || reducerKeys.length !== Object.keys(state).length
    return hasChanged ? nextState : state
  }
}
